import { Box, Spinner } from '@chakra-ui/react';
import { PaginatedResponse, Params } from '@partiu/api/types';
import { cloneElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { UseMutationOptions, UseMutationResult } from 'react-query';
import { useTheme } from '../../contexts/ThemeProvider';

const order = [
  'Época Negócios',
  'Pequenas Empresas & Grandes Negócios',
  'Galileu',
  'Marie Claire',
  'Auto Esporte',
  'Globo Rural',
];

export function InfiniteScroll<T>({
  mutationHook,
  renderItem,
  emptyResultsComponent = <></>,
  keyExtraction,
  scrollPercentageTrigger = 0.8,
  children,
  filterMagazine = false,
  hookProps = {},
}: IInfiniteScrollProps<T>) {
  const [page, setPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const { colors } = useTheme();
  const [resultDict, setResultDict] = useState<Record<number, T[]>>({});
  const [done, setDone] = useState(false);
  const isLoadingRef = useRef(false);

  const serializedHookProps = useMemo(() => JSON.stringify(hookProps ?? {}), [hookProps]);
  const [filtersKey, setFiltersKey] = useState(serializedHookProps);

  const mutation = mutationHook({
    onSuccess(data) {
      isLoadingRef.current = false;

      setResultDict((prevDict) => {
        const newDict = page === 1 ? {} : { ...prevDict };

        let ordenerdResults = data.results.filter((item) => {
          const i = item as Record<string, unknown>;
          const book = i.book as Record<string, unknown> | undefined;
          return book?.cover || book?.coverOptimized || i.cover || i.coverOptimized;
        }) as T[];

        if (filterMagazine) {
          const grouped: Record<string, T[]> = {};
          data.results.forEach((item) => {
            const name = (item as { name?: string }).name || '';
            if (!grouped[name]) grouped[name] = [];
            grouped[name].push(item);
          });

          ordenerdResults = [];
          let added = true;
          while (added) {
            added = false;
            for (const magazine of order) {
              if (grouped[magazine] && grouped[magazine].length > 0) {
                ordenerdResults.push(grouped[magazine].shift() as T);
                added = true;
              }
            }
          }
        }

        newDict[page] = [...ordenerdResults];
        return newDict;
      });

      const nextPage = 'next' in data ? (data as { next?: string | null }).next : undefined;
      setHasNextPage(Boolean(nextPage));
      setIsInitialLoading(false);
    },
    onError() {
      isLoadingRef.current = false;
      setDone(true);
      setIsInitialLoading(false);
    },
  });

  const onScroll = useCallback(() => {
    const scroll = window.scrollY + window.innerHeight;
    const scrollHeight = document.body.scrollHeight;
    const percentage = scroll / scrollHeight;

    if (isLoadingRef.current || done || !hasNextPage) return;

    if (percentage >= scrollPercentageTrigger) {
      isLoadingRef.current = true;
      setPage((prev) => prev + 1);
    }
  }, [done, hasNextPage, scrollPercentageTrigger]);

  useEffect(() => {
    document.addEventListener('scroll', onScroll);
    return () => document.removeEventListener('scroll', onScroll);
  }, [onScroll]);

  // Reset state when filters change
  useEffect(() => {
    if (filtersKey === serializedHookProps) return;

    setFiltersKey(serializedHookProps);
    setResultDict({});
    setHasNextPage(false);
    setPage(1);
    setDone(false);
    setIsInitialLoading(true);
  }, [serializedHookProps, filtersKey]);

  // Fetch data when page or filters change
  useEffect(() => {
    mutation.mutate({ page, ...hookProps });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, filtersKey]);

  const results = Object.values(resultDict).reduce<T[]>((acc, current = []) => [...acc, ...current], []);

  if (isInitialLoading)
    return (
      <Box w={'100%'} display={'flex'} justifyContent={'center'} alignItems={'center'}>
        <Spinner color={colors.primaryColor} size={'lg'} />
      </Box>
    );

  if (!mutation.isLoading && results.length === 0) return emptyResultsComponent;

  return (
    <>
      {children(results.map((r: T) => cloneElement(renderItem(r), { key: keyExtraction(r) })))}
      {mutation.isLoading && (
        <Box w="100%" display="flex" justifyContent="center" py={4}>
          <Spinner color={colors.primaryColor} size="md" />
        </Box>
      )}
    </>
  );
}

interface IInfiniteScrollProps<T> {
  mutationHook: (
    options: UseMutationOptions<PaginatedResponse<T>, unknown, Params, unknown>
  ) => UseMutationResult<PaginatedResponse<T>, unknown, Params, unknown>;
  renderItem: (item: T) => JSX.Element;
  scrollPercentageTrigger?: number;
  children: (items: JSX.Element[]) => JSX.Element;
  hookProps?: Params;
  keyExtraction: (t: T) => string;
  emptyResultsComponent?: JSX.Element;
  filterMagazine?: boolean;
}
